import { db } from "@/lib/firebase";
import {
  extractDomain,
  normalizeUrlForComparison,
  prepareValidUrl,
} from "@/lib/utils";
import {
  Domain,
  Page,
  parseUserData,
  UserData,
} from "@/schemas";
import { User } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

// Deduplicates concurrent calls to ensureDomainAndPageExists for the same URL
const _ensureInFlight = new Map<string, Promise<{ domainId: string; pageId: string }>>();

export interface userSlice {
  /* state */
  currentUser: User | null;
  userData: UserData | null;
  isUserLoading: boolean;
  error: string | null;

  /* actions */
  setCurrentUser: (user: User | null) => void;
  subscribeToUserData: (userId: string) => () => void;
  setCurrentDomainId: (domainId: string | null) => Promise<void>;
  setCurrentPageId: (pageId: string | null) => Promise<void>;
  deleteCurrentDomain: () => Promise<void>;
  ensureDomainAndPageExists: (
    url: string,
  ) => Promise<{ domainId: string; pageId: string }>;
  markWizardComplete: () => Promise<void>;
  setEmailVerificationResendCooldown: () => void;
  getEmailVerificationCooldownRemaining: () => number;

  /* internals */
  _userDataUnsubscribe: (() => void) | null;
}

/* ------------------------------------------------------------------ */

const COOLDOWN_DURATION_MS = 60 * 1000; // 1 minute
const EMAIL_VERIFICATION_COOLDOWN_KEY =
  "crolytics-email-verification-resend-cooldown-expiry";

/**
 * Standalone helper to set cooldown in localStorage.
 * Use this for synchronous writes that must happen before navigation.
 */
export function setEmailCooldownInStorage(): void {
  const expiry = Date.now() + COOLDOWN_DURATION_MS;
  localStorage.setItem(EMAIL_VERIFICATION_COOLDOWN_KEY, String(expiry));
}

/**
 * Standalone helper to read cooldown remaining from localStorage.
 * Use this for synchronous initial reads (e.g., in useState initializers)
 * to avoid any timing issues with store hydration during navigation.
 */
export function getEmailCooldownFromStorage(): number {
  const stored = localStorage.getItem(EMAIL_VERIFICATION_COOLDOWN_KEY);
  if (!stored) return 0;
  const expiry = Number(stored);
  if (isNaN(expiry)) return 0;
  const remaining = expiry - Date.now();
  if (remaining <= 0) {
    localStorage.removeItem(EMAIL_VERIFICATION_COOLDOWN_KEY);
    return 0;
  }
  return Math.ceil(remaining / 1000);
}

export const createAuthSlice: StateCreator<StoreState, [], [], userSlice> = (
  set,
  get,
) => ({
  _userDataUnsubscribe: null,

  currentUser: null,
  userData: null,
  isUserLoading: true,
  error: null,

  /* ---------- auth ---------- */
  setCurrentUser: (user) => {
    const previousUserId = get().currentUser?.uid;
    set({ currentUser: user });

    if (user) {
      if (user.uid !== previousUserId) {
        get().subscribeToUserData(user.uid);
      }
    } else {
      /* logout cleanup */
      get()._userDataUnsubscribe?.();
      get().unsubscribeDomains();
      get().unsubscribePages();
      get().unsubscribeJobs();
      get().unsubscribePersonas();
      get().unsubscribeOffers();
      get().unsubscribePolicies();
      get().resetGA4State();
      set({
        currentUser: null,
        userData: null,
        isUserLoading: false,
        domains: [],
        currentDomain: null,
        pages: [],
        currentPage: null,
        jobs: {},
        currentJobId: null,
        pendingWorkspaceSelection: null,
      });
    }
  },

  /* ---------- user-doc listener ---------- */
  subscribeToUserData: (userId) => {
    /* replace any existing listener */
    get()._userDataUnsubscribe?.();

    set({ isUserLoading: true });
    const userRef = doc(db, "users", userId);

    const unsubscribe = onSnapshot(
      userRef,
      async (snap) => {
        if (!snap.exists()) {
          set({ isUserLoading: false, userData: null });
          get().unsubscribeDomains();
          get().unsubscribePages();
          return;
        }

        try {
          const newData = parseUserData(snap.data());

          /* shallow compare JSON – cheap for this object */
          if (JSON.stringify(get().userData) !== JSON.stringify(newData)) {
            console.log("Real-time user data update:", newData);
            set({ userData: newData, isUserLoading: false, error: null });

            // No domains listener here. Domains are org-scoped
            // (organizations/{orgId}/domains), and `useStoreInitializer` owns
            // that subscription, keyed on the resolved orgId. This used to call
            // `subscribeToDomains(userId)` — a leftover from when domains lived
            // under users/{uid} — which raced the org lookup: the user doc is a
            // single fast read, so it started a listener on
            // organizations/{uid}/domains before the orgId existed, got
            // permission-denied, and left `domainError` set on the store.
          } else {
            set({ isUserLoading: false });
          }
        } catch (error) {
          console.error(`UserData validation failed for user ${userId}:`, {
            error: error instanceof Error ? error.message : error,
            rawData: snap.data(),
          });
          set({
            error: `User data validation failed: ${error instanceof Error ? error.message : "Unknown error"}`,
            isUserLoading: false,
          });
          // Don't throw here - this is already in the snapshot success handler
          // Just set error state for UI to handle
        }
      },
      (err) => {
        console.error(err);
        set({
          error: "Failed to subscribe to user data",
          isUserLoading: false,
        });
      },
    );

    set({
      _userDataUnsubscribe: unsubscribe,
    });

    return unsubscribe;
  },

  // Set the current domain ID in Firestore and trigger domain slice update
  setCurrentDomainId: async (domainId) => {
    const { currentUser, userData } = get();
    if (!currentUser || userData?.currentDomainId === domainId) return;

    console.log(`Setting current domain ID in Firestore to: ${domainId}`);
    try {
      get().clearPendingWorkspaceSelection();

      const userRef = doc(db, "users", currentUser.uid);
      // When domainId is null, updates sets both to null
      const updates =
        domainId != null
          ? { currentDomainId: domainId, currentPageId: null }
          : { currentDomainId: null, currentPageId: null };
      await updateDoc(userRef, updates);

      // Update local state optimistically (Firestore listener will confirm)
      set((s) => ({
        userData: s.userData
          ? { ...s.userData, currentDomainId: domainId, currentPageId: null }
          : null,
      }));
      // Firestore listener (`subscribeToUserData`) will update the local state (userData)
      // and `useStoreInitializer` will react to that state change to update
      // domain/page subscriptions and current items.
    } catch (error) {
      console.error("Error setting current domain ID in Firestore:", error);
      set({ error: "Failed to set current domain ID" });
      // Optionally revert optimistic update or show error to user
    }
  },

  // Set the current page ID in Firestore and trigger page slice update
  setCurrentPageId: async (pageId) => {
    const { currentUser } = get();
    // Ensure user is logged
    if (!currentUser) {
      console.warn("Cannot set current page ID: No currentUser");
      return;
    }

    console.log(`Setting current page ID in Firestore to: ${pageId}`);
    try {
      if (pageId == null) {
        get().clearPendingWorkspaceSelection();
      }

      const userRef = doc(db, "users", currentUser.uid);
      // ⬇️ inside setCurrentPageId (same idea)
      const updates =
        pageId != null ? { currentPageId: pageId } : { currentPageId: null };
      await updateDoc(userRef, updates);

      // Update local state optimistically
      // Add explicit type for state
      set((s: StoreState) => ({
        userData: s.userData
          ? { ...s.userData, currentPageId: pageId ?? null }
          : null,
      }));
      // Firestore listener (`subscribeToUserData`) will update the local state (userData)
      // and `useStoreInitializer` will react to that state change to update
      // job subscriptions and the current page item.
    } catch (error) {
      console.error("Error setting current page ID in Firestore:", error);
      set({ error: "Failed to set current page ID" });
    }
  },

  // Clear current domain/page in Firestore and trigger slice updates
  deleteCurrentDomain: async () => {
    const { currentUser, userData } = get();
    if (!currentUser || !userData?.currentDomainId) return; // No domain to clear

    console.log("Clearing current domain and page ID in Firestore.");
    try {
      get().clearPendingWorkspaceSelection();

      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        currentDomainId: "",
        currentPageId: "",
      });

      // Update local state optimistically
      // Add explicit type for state
      set((state: StoreState) => ({
        userData: state.userData
          ? { ...state.userData, currentDomainId: "", currentPageId: "" }
          : null,
      }));

      // Trigger updates in domain and page slices
      await get().setCurrentDomain(null);
      await get().setCurrentPage(null);
    } catch (error) {
      console.error("Error clearing current domain/page:", error);
      set({ error: "Failed to clear current domain/page selection" });
    }
  },

  /* ---------- ensureDomainAndPageExists ---------- */
  ensureDomainAndPageExists: async (
    url: string,
  ): Promise<{ domainId: string; pageId: string }> => {
    const normalizedKey = normalizeUrlForComparison(url);
    const inFlight = _ensureInFlight.get(normalizedKey);
    if (inFlight) return inFlight;

    const promise = (async () => {
      const { currentUser, orgId, domains, addDomain, addPage } = get();
      if (!currentUser) {
        throw new Error("User not authenticated");
      }
      if (!orgId) {
        throw new Error("User does not belong to an organization");
      }

      console.log("[ensureDomainAndPageExists] Starting with URL:", url);

      const domainUrl = extractDomain(url);
      const pageUrl = prepareValidUrl(url);

      console.log("[ensureDomainAndPageExists] Extracted:", {
        domainUrl,
        pageUrl,
      });

      /* 1. domain */
      let domain = domains.find((d) => d.url === domainUrl);
      if (!domain) {
        console.log(
          "[ensureDomainAndPageExists] Domain not found, creating new domain:",
          domainUrl,
        );
        const id = await addDomain(orgId, domainUrl);
        const snap = await getDoc(
          doc(db, `organizations/${orgId}/domains`, id),
        );
        domain = { id: snap.id, ...(snap.data() as Omit<Domain, "id">) };
        console.log("[ensureDomainAndPageExists] Created domain:", domain.id);
      } else {
        console.log(
          "[ensureDomainAndPageExists] Found existing domain:",
          domain.id,
        );
      }

      /* 2. page — always check Firestore for dedup */
      let page: Page | undefined;
      const normalizedPageUrl = normalizeUrlForComparison(pageUrl);
      console.log(
        "[ensureDomainAndPageExists] Looking for page with normalized URL:",
        normalizedPageUrl,
      );

      console.log(
        "[ensureDomainAndPageExists] Fetching pages from Firestore for domain:",
        domain.id,
      );
      const pagesSnap = await getDocs(
        collection(db, `organizations/${orgId}/domains/${domain.id}/pages`),
      );
      console.log(
        "[ensureDomainAndPageExists] Fetched pages count:",
        pagesSnap.size,
      );
      pagesSnap.forEach((d) => {
        const existingUrl = d.data().url as string;
        const normalizedExisting = normalizeUrlForComparison(existingUrl);
        console.log("[ensureDomainAndPageExists] Comparing page:", {
          pageId: d.id,
          existingUrl,
          normalizedExisting,
          match: normalizedExisting === normalizedPageUrl,
        });
        if (normalizedExisting === normalizedPageUrl) {
          page = { id: d.id, ...d.data() } as Page;
          console.log(
            "[ensureDomainAndPageExists] Found matching page:",
            page.id,
          );
        }
      });

      if (!page) {
        console.log(
          "[ensureDomainAndPageExists] No existing page found, creating new page:",
          pageUrl,
        );
        const id = await addPage(orgId, domain.id, pageUrl);
        page = {
          id,
          url: pageUrl,
          created_at: serverTimestamp(),
          domainId: domain.id,
        } as Page;
        console.log("[ensureDomainAndPageExists] Created page:", page.id);
      } else {
        console.log(
          "[ensureDomainAndPageExists] Reusing existing page:",
          page.id,
        );
      }

      /* 3. write IDs if needed */
      get().setPendingWorkspaceSelection({
        domainId: domain.id,
        pageId: page.id,
        url: pageUrl,
      });

      const uData = get().userData;
      if (
        uData?.currentDomainId !== domain.id ||
        uData?.currentPageId !== page.id
      ) {
        console.log("[ensureDomainAndPageExists] Updating user document with:", {
          domainId: domain.id,
          pageId: page.id,
        });
        await updateDoc(doc(db, "users", currentUser.uid), {
          currentDomainId: domain.id,
          currentPageId: page.id,
        });
      }

      console.log("[ensureDomainAndPageExists] Completed successfully:", {
        domainId: domain.id,
        pageId: page.id,
      });

      return { domainId: domain.id, pageId: page.id };
    })();

    _ensureInFlight.set(normalizedKey, promise);
    try {
      return await promise;
    } finally {
      _ensureInFlight.delete(normalizedKey);
    }
  },

  // Mark wizard as complete
  markWizardComplete: async () => {
    const { currentUser } = get();
    if (!currentUser) return;

    try {
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        hasCompletedSetupWizard: true,
      });

      // Update local state optimistically
      set((state: StoreState) => ({
        userData: state.userData
          ? {
              ...state.userData,
              hasCompletedSetupWizard: true,
            }
          : null,
      }));

      console.log("Setup wizard marked as complete");
    } catch (error) {
      console.error("Error marking wizard as complete:", error);
      set({ error: "Failed to mark wizard as complete" });
      throw error;
    }
  },

  // Set email verification resend cooldown (uses localStorage directly for sync access)
  setEmailVerificationResendCooldown: () => {
    const expiry = Date.now() + COOLDOWN_DURATION_MS;
    localStorage.setItem(EMAIL_VERIFICATION_COOLDOWN_KEY, String(expiry));
  },

  // Get remaining cooldown time in seconds (reads from localStorage synchronously)
  getEmailVerificationCooldownRemaining: () => {
    const stored = localStorage.getItem(EMAIL_VERIFICATION_COOLDOWN_KEY);
    if (!stored) return 0;
    const expiry = Number(stored);
    if (isNaN(expiry)) return 0;
    const remaining = expiry - Date.now();
    if (remaining <= 0) {
      localStorage.removeItem(EMAIL_VERIFICATION_COOLDOWN_KEY);
      return 0;
    }
    return Math.ceil(remaining / 1000);
  },
});
